import { useNostr } from '@nostrify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import type { NostrEvent } from '@nostrify/nostrify';

export interface Playlist {
  event: NostrEvent;
  id: string;
  title: string;
  description: string;
  image?: string;
  trackAddresses: string[]; // Array of "a" tag values (30999:pubkey:dTag)
  trackCount: number;
  createdAt: number;
}

function parsePlaylist(event: NostrEvent): Playlist | null {
  if (event.kind !== 30004) return null;

  const dTag = event.tags.find(([name]) => name === 'd')?.[1];
  const title = event.tags.find(([name]) => name === 'title')?.[1];
  
  if (!dTag || !title) return null;

  const description = event.tags.find(([name]) => name === 'description')?.[1] || '';
  const image = event.tags.find(([name]) => name === 'image')?.[1];
  const trackAddresses = event.tags
    .filter(([name]) => name === 'a')
    .map(([_, addr]) => addr)
    .filter(addr => addr.startsWith('30999:'));

  return {
    event,
    id: dTag,
    title,
    description,
    image,
    trackAddresses,
    trackCount: trackAddresses.length,
    createdAt: event.created_at,
  };
}

export function useUserPlaylists() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['user-playlists', user?.pubkey],
    queryFn: async () => {
      if (!user) return [];

      const events = await nostr.query([{
        kinds: [30004],
        authors: [user.pubkey],
        limit: 100,
      }]);

      const playlists = events
        .map(parsePlaylist)
        .filter((p): p is Playlist => p !== null)
        .sort((a, b) => b.createdAt - a.createdAt);

      return playlists;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2,
  });
}

export function usePlaylist(pubkey: string, dTag: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['playlist', pubkey, dTag],
    queryFn: async () => {
      const events = await nostr.query([{
        kinds: [30004],
        authors: [pubkey],
        '#d': [dTag],
        limit: 1,
      }]);

      if (events.length === 0) return null;
      return parsePlaylist(events[0]);
    },
    enabled: !!pubkey && !!dTag,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreatePlaylist() {
  const { mutate } = useNostrPublish();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async ({
      title,
      description,
      image,
      trackAddresses,
    }: {
      title: string;
      description?: string;
      image?: string;
      trackAddresses: string[];
    }) => {
      return new Promise<void>((resolve, reject) => {
        const dTag = title.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now();
        
        const tags: string[][] = [
          ['d', dTag],
          ['title', title],
        ];

        if (description) {
          tags.push(['description', description]);
        }

        if (image) {
          tags.push(['image', image]);
        }

        trackAddresses.forEach(addr => {
          tags.push(['a', addr]);
        });

        mutate(
          {
            kind: 30004,
            content: '',
            tags,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['user-playlists', user?.pubkey] });
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    },
  });
}

export function useUpdatePlaylist() {
  const { mutate } = useNostrPublish();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async ({
      playlistId,
      title,
      description,
      image,
      trackAddresses,
    }: {
      playlistId: string;
      title: string;
      description?: string;
      image?: string;
      trackAddresses: string[];
    }) => {
      return new Promise<void>((resolve, reject) => {
        const tags: string[][] = [
          ['d', playlistId],
          ['title', title],
        ];

        if (description) {
          tags.push(['description', description]);
        }

        if (image) {
          tags.push(['image', image]);
        }

        trackAddresses.forEach(addr => {
          tags.push(['a', addr]);
        });

        mutate(
          {
            kind: 30004,
            content: '',
            tags,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['user-playlists', user?.pubkey] });
              queryClient.invalidateQueries({ queryKey: ['playlist', user?.pubkey, playlistId] });
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    },
  });
}

export function useAddTrackToPlaylist() {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const updatePlaylist = useUpdatePlaylist();

  return useMutation({
    mutationFn: async ({
      playlist,
      trackAddress,
    }: {
      playlist: Playlist;
      trackAddress: string;
    }) => {
      if (playlist.trackAddresses.includes(trackAddress)) {
        throw new Error('Track already in playlist');
      }

      await updatePlaylist.mutateAsync({
        playlistId: playlist.id,
        title: playlist.title,
        description: playlist.description,
        image: playlist.image,
        trackAddresses: [...playlist.trackAddresses, trackAddress],
      });
    },
  });
}
